import React, { isValidElement } from "react"
import { createStyles } from "@mantine/core"

const useStyles = createStyles(
    (
        theme,
        {
            center,
            truncate,
            numeric,
            lineClamp,
        }: { center: boolean; truncate: boolean; numeric: boolean; lineClamp: number },
    ) => ({
        wrapper: {
            display: "flex",
            justifyContent: center ? "center" : "flex-start",
            alignItems: "center",
            width: "100%",
            flexGrow: 1,
            height: "100%",
        },
        content: {
            flex: center ? "unset" : 1,
            textAlign: numeric ? "right" : undefined,
            ...(truncate
                ? {
                      WebkitLineClamp: lineClamp ?? 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      wordBreak: "break-word",
                  }
                : {}),
        },
    }),
)

interface CellWrapperProps extends React.HTMLProps<HTMLDivElement> {
    center?: boolean
    children?: React.ReactNode
    className?: string
    truncate?: boolean
    isGrouped?: boolean
    groupedAmount?: number
    groupedCellValue?: string
    formattingFn?: (value: string | number) => string | number
    row?: React.ReactNode
    isNumeric?: boolean
    lineClamp?: number
    isIndexCell?: boolean
    columnDataType?: any
    menuShouldScrollIntoView?: boolean
}

/**
 * @function CellWrapper
 * @description Should be rendered directly inside of a Td in a MantineReactTable sheet.
 * The outer div applies the padding and assures the contents fill the <td> element.
 * The inner div ensures the contents are wrapped and truncated at two lines.
 *
 * @param {CellWrapperProps} props
 * @param {boolean} props.center - Allows for the contents of a cell to be centered instead of left aligned (CheckboxCell)
 * @param {React.ReactNode} props.children - The contents of the cell
 * @param {string} props.className - The className to apply to the outer div
 * @param {boolean} props.truncate - Whether or not to truncate the contents of the cell at two lines
 * @param {Function} props.formattingFn - a formatting function for groupedCellValue
 *
 * @returns {JSX.Element}
 */
export const CellWrapper = ({
    center,
    children,
    className,
    truncate = true,
    isGrouped,
    groupedAmount,
    formattingFn = (x) => x,
    row,
    isNumeric,
    columnDataType,
    lineClamp,
    isIndexCell,
    menuShouldScrollIntoView,
    ...props
}: CellWrapperProps): JSX.Element => {
    const { classes, cx } = useStyles({ center, truncate, numeric: isNumeric, lineClamp })

    const renderChildren = () => {
        if (isGrouped) {
            return children === "undefined" || children === "null" ? "None" : children
        }
        // elements can only be rendered if they are a specific value, or a valid
        // react element, non-element objects will throw an error, so catch it here
        if (typeof children !== "object" || isValidElement(children) || isIndexCell) {
            return children
        }
    }

    return (
        <div {...props} className={cx(classes.wrapper, className, "q-cell-wrapper")}>
            <div className={classes.content}>
                {renderChildren()}
                {isGrouped && ` (${groupedAmount})`}
            </div>
        </div>
    )
}
